import { useCallback } from 'react'

import { useApolloClient } from '@apollo/client'

import {
  EmployeeJobCatalogTreeLevelsFilterDocument,
  type EmployeeJobCatalogTreeLevelsFilterQuery,
} from 'generated/resources/graphql'

type ExtractedJobRoleFilterValues = {
  jobCatalogTreeNodeIds?: string[]
  jobLevelIds?: string[]
}

const toUniqueIds = (values: unknown): string[] | undefined => {
  if (!Array.isArray(values)) return undefined

  const normalizedIds = values
    .filter(
      (value): value is string | number => typeof value === 'string' || typeof value === 'number'
    )
    .map((value) => String(value))

  return normalizedIds.length ? [...new Set(normalizedIds)] : undefined
}

export const mergeUniqueIds = (...idGroups: Array<string[] | undefined>): string[] | undefined => {
  const mergedIds = idGroups.flatMap((ids) => ids ?? [])

  return mergedIds.length ? [...new Set(mergedIds)] : undefined
}

type TreeLevelNode = NonNullable<
  EmployeeJobCatalogTreeLevelsFilterQuery['jobCatalog']['treeLevelsConnection']
>['nodes'][number]

const buildRoleLevelMap = (treeLevels?: TreeLevelNode[] | null) => {
  const roleLevelMap: Record<string, string[]> = {}

  for (const treeLevel of treeLevels ?? []) {
    const roleUuid = treeLevel.ancestor?.uuid

    if (!roleUuid) {
      continue
    }

    roleLevelMap[roleUuid] ??= []
    roleLevelMap[roleUuid].push(treeLevel.uuid)
  }

  return roleLevelMap
}

export const useExtractJobRoleFilterValues = ({
  hasJobCatalogTreeFeature,
}: {
  hasJobCatalogTreeFeature: boolean
}) => {
  const client = useApolloClient()

  return useCallback(
    (filters: unknown): ExtractedJobRoleFilterValues => {
      if (!hasJobCatalogTreeFeature) {
        return {
          jobLevelIds: toUniqueIds(Reflect.get(Object(filters), 'jobRole')),
        }
      }

      const selectedRoleIds = toUniqueIds(Reflect.get(Object(filters), 'jobRole'))
      const selectedLevelIds = toUniqueIds(Reflect.get(Object(filters), 'jobRoleLevel'))

      let cachedData: EmployeeJobCatalogTreeLevelsFilterQuery | null = null

      try {
        cachedData = client.readQuery<EmployeeJobCatalogTreeLevelsFilterQuery>({
          query: EmployeeJobCatalogTreeLevelsFilterDocument,
        })
      } catch {
        cachedData = null
      }

      const roleLevelMap = buildRoleLevelMap(cachedData?.jobCatalog.treeLevelsConnection?.nodes)
      const expandedLevelIds = selectedRoleIds?.flatMap((roleId) => roleLevelMap[roleId] ?? [])

      return {
        jobCatalogTreeNodeIds: mergeUniqueIds(expandedLevelIds, selectedLevelIds),
      }
    },
    [client, hasJobCatalogTreeFeature]
  )
}
